import { forwardRef, useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Crown, Download, Loader2, RotateCcw } from "lucide-react";
import { CWeekBadge } from "./CWeekBadge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useApi } from "../../hooks/useApi";
import { height, fmt, shortDate, positionsOf } from "./kit";

// A 2.5 x 3.5 in basketball card, drawn at 340 x 476 px (downloads at 3x: 1020 x 1428).
const W = 340, H = 476;

// Editions come only from stats (no manual override), so recruiters can trust them.
const fx = (v) => Number(v ?? 0).toFixed(1);
export const editionOf = (s) => {
  const ppg = Number(s?.ppg ?? 0), rpg = Number(s?.rpg ?? 0), apg = Number(s?.apg ?? 0), ts = s?.ts_pct == null ? null : Number(s.ts_pct);
  if (!s?.gp) return { tier: "base" };
  if (ppg >= 25) return { tier: "platinum", reason: `${fx(ppg)} PPG` };
  if (ppg >= 20 && ts !== null && ts >= 60) return { tier: "platinum", reason: `${fx(ppg)} PPG · ${fx(ts)} TS%` };
  if (ppg >= 20) return { tier: "gold", reason: `${fx(ppg)} PPG` };
  if (ppg >= 10 && rpg >= 10) return { tier: "gold", reason: `${fx(ppg)} PPG · ${fx(rpg)} RPG double-double` };
  if (ppg >= 10 && apg >= 10) return { tier: "gold", reason: `${fx(ppg)} PPG · ${fx(apg)} APG double-double` };
  return { tier: "base" };
};

const THEMES = {
  base: { accent: "#39FF14", soft: "rgba(57,255,20,0.15)", line: "rgba(57,255,20,0.6)", glow: "rgba(57,255,20,0.18)",
    frame: "linear-gradient(135deg, #39FF14, #0E7A0A 45%, #39FF14 70%, #B9FF9F)", bg: "radial-gradient(circle at 50% 35%, #1E3A2A, #0A0E1A 70%)" },
  gold: { accent: "#F5C542", soft: "rgba(245,197,66,0.16)", line: "rgba(245,197,66,0.7)", glow: "rgba(245,197,66,0.3)", label: "Gold edition",
    frame: "linear-gradient(135deg, #FFF1B8, #C8961E 28%, #FFE27A 50%, #8A5E0B 74%, #FFE9A6)", bg: "radial-gradient(circle at 50% 35%, #3A2E12, #0A0E1A 70%)" },
  platinum: { accent: "#E5E7EB", soft: "rgba(229,231,235,0.16)", line: "rgba(229,231,235,0.75)", glow: "rgba(186,230,253,0.35)", label: "Platinum edition",
    frame: "linear-gradient(135deg, #FFFFFF, #94A3B8 26%, #F1F5F9 46%, #64748B 70%, #E2E8F0 88%, #BAE6FD)", bg: "radial-gradient(circle at 50% 35%, #2A3340, #0A0E1A 70%)" },
};
const vars = (t) => ({ "--tc-accent": t.accent, "--tc-soft": t.soft, "--tc-line": t.line });

const seasonLabel = () => {
  const d = new Date();
  const y = d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
};
const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const pctOf = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}`);
const num = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? "—" : fmt(v));

const Foil = ({ live }) => (
  <>
    <div className="tc-holo pointer-events-none absolute inset-0 rounded-[18px]" style={live ? undefined : { opacity: 0.3, backgroundPosition: "40% 40%, 0 0" }} />
    {live && <div className="tc-glare pointer-events-none absolute inset-0 rounded-[18px]" />}
  </>
);

const EditionBadge = ({ t, className = "" }) => (t.label ? (
  <span data-testid="trading-card-edition-badge" style={{ background: t.frame }}
    className={`inline-block rounded-sm px-2 py-0.5 font-mono-gd text-[10px] font-black uppercase tracking-[0.18em] text-[#0A0E1A] shadow-[0_2px_10px_rgba(0,0,0,0.45)] ${className}`}>{t.label}</span>
) : null);

export const CardFront = forwardRef(({ p, teamName, photo, edition, live = false }, ref) => {
  const [bad, setBad] = useState(false);
  const ed = edition ?? { tier: "base" };
  const t = THEMES[ed.tier];
  return (
  <div ref={ref} data-testid="trading-card-front" data-edition={ed.tier} style={{ width: W, height: H, ...vars(t) }} className={`tc-${ed.tier} relative overflow-hidden rounded-[18px] bg-[#0A0E1A] p-[9px] font-sans`}>
    <div className="absolute inset-0 rounded-[18px]" style={{ background: t.frame }} />
    <div className="relative h-full w-full overflow-hidden rounded-[11px] bg-[#0F172A]">
      {photo && !bad ? (
        <img src={photo} alt="" crossOrigin="anonymous" onError={() => setBad(true)} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: t.bg }}>
          <span className="absolute -right-4 top-6 font-display text-[260px] font-black leading-none text-[var(--tc-soft)]" style={{ WebkitTextStroke: `2px ${t.line}` }}>{p.jersey ?? "00"}</span>
          <span className="relative -mt-16 flex h-32 w-32 items-center justify-center rounded-full border-2 border-[var(--tc-line)] bg-[#0A0E1A]/70 font-display text-[64px] font-black text-white">{initials(p.name)}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-[#0A0E1A] via-[#0A0E1A]/80 to-transparent" />
      <div className="absolute left-3 top-3 rounded-md bg-[#0A0E1A]/85 px-2 py-1 backdrop-blur-sm">
        <p className="font-display text-[15px] font-black uppercase leading-none tracking-wide text-white">GAME<span className="text-[#FF3B30]">DAY</span></p>
        <p className="font-mono-gd text-[9px] uppercase tracking-[0.2em] text-[var(--tc-accent)]">{seasonLabel()}</p>
      </div>
      <div className="absolute right-3 top-3 flex h-12 w-12 flex-col items-center justify-center rounded-full border-2 border-[var(--tc-accent)] bg-[#0A0E1A]/90">
        <span className="font-mono-gd text-[9px] uppercase leading-none text-slate-300">No.</span>
        <span className="font-display text-[22px] font-black leading-none text-white">{p.jersey ?? "–"}</span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <EditionBadge t={t} />
          {p.primary_archetype && <span className="inline-block rounded-sm bg-[var(--tc-accent)] px-2 py-0.5 font-mono-gd text-[11px] font-bold uppercase tracking-wider text-[#0A0E1A]">{p.primary_archetype}</span>}
          {(Array.isArray(p.leader_badges) ? p.leader_badges : []).map((b) => (
            <span key={b.key} data-testid={`trading-card-leader-${b.key}`} className="inline-flex items-center gap-1 rounded-sm border border-[#F5C542] bg-[#0A0E1A]/85 px-1.5 py-0.5 font-mono-gd text-[10px] font-bold uppercase tracking-wider text-[#F5C542]">
              <Crown className="h-3 w-3" />{b.label} leader
            </span>
          ))}
          {p.c_week && <CWeekBadge cw={p.c_week} testid="trading-card-c-week" compact />}
        </div>
        <p data-testid="trading-card-name" className="font-display text-[40px] font-black uppercase leading-[0.9] text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">{p.name ?? "Player"}</p>
        <div className="mt-2 flex items-center gap-2 border-t border-[var(--tc-line)] pt-2 font-mono-gd text-[12px] uppercase tracking-wider text-slate-200">
          <span className="font-bold text-[var(--tc-accent)]">{positionsOf(p)}</span><span className="text-slate-500">|</span>
          <span className="truncate">{teamName ?? "GAMEDAY"}</span>
        </div>
      </div>
    </div>
    <Foil live={live} />
  </div>
  );
});
CardFront.displayName = "CardFront";

const Box = ({ label, value, accent }) => (
  <div className={`rounded-md px-1 py-1.5 text-center ${accent ? "bg-[var(--tc-accent)] text-[#0A0E1A]" : "bg-[#162032] text-white"}`}>
    <p className={`font-mono-gd text-[9px] font-bold uppercase tracking-wider ${accent ? "text-[#0A0E1A]/80" : "text-slate-400"}`}>{label}</p>
    <p className="font-display text-[20px] font-black leading-none">{value}</p>
  </div>
);

const LOG_COLS = [["PTS", "pts"], ["REB", "reb"], ["AST", "ast"], ["EFF", "eff"]];
const MAX_ROWS = 7;

const GameLog = ({ games, s, max }) => {
  const rows = [...games].sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? ""))).slice(0, max);
  return (
    <table data-testid="trading-card-log" className="w-full table-fixed font-mono-gd text-[12px] leading-tight text-slate-200">
      <thead><tr className="bg-[var(--tc-soft)] text-[10px] uppercase text-[var(--tc-accent)]">
        <th className="w-[52px] py-1 pl-1.5 text-left">Date</th><th className="py-1 text-left">Opp</th>
        {LOG_COLS.map(([h]) => <th key={h} className="w-[34px] py-1 text-right last:pr-1.5">{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.length ? rows.map((g, i) => (
          <tr key={g.game_id ?? i} className="border-b border-[#1E293B]">
            <td className="py-[3px] pl-1.5">{shortDate(g.date).replace(/,? \d{4}$/, "")}</td>
            <td className="truncate py-[3px] pr-1 font-sans">{g.opponent ?? "—"}</td>
            {LOG_COLS.map(([h, k]) => <td key={h} className="py-[3px] text-right last:pr-1.5">{g[k] ?? 0}</td>)}
          </tr>
        )) : <tr><td colSpan={6} className="py-3 text-center font-sans text-slate-400">No games logged yet</td></tr>}
        <tr className="bg-[#162032] font-bold text-white">
          <td className="py-1 pl-1.5" colSpan={2}>Season · {s.gp ?? 0} GP</td>
          {["ppg", "rpg", "apg", "eff"].map((k) => <td key={k} className="py-1 text-right last:pr-1.5">{k === "eff" && s.gp ? fmt(s.eff / s.gp) : num(s[k])}</td>)}
        </tr>
      </tbody>
    </table>
  );
};

export const CardBack = forwardRef(({ p, teamName, season, perGame, edition, live = false, publicView = false }, ref) => {
  const s = season ?? {};
  const games = Array.isArray(perGame) ? perGame : [];
  const ed = edition ?? { tier: "base" };
  const t = THEMES[ed.tier];
  const maxRows = t.label ? MAX_ROWS - 1 : MAX_ROWS;
  return (
    <div ref={ref} data-testid="trading-card-back" data-edition={ed.tier} style={{ width: W, height: H, background: t.frame, ...vars(t) }} className={`tc-${ed.tier} relative overflow-hidden rounded-[18px] p-[9px] font-sans`}>
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[11px] bg-[#0A0E1A] bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.025)_0_2px,transparent_2px_10px)]">
        <div className="flex items-center gap-3 px-3 py-2 text-[#0A0E1A]" style={{ background: ed.tier === "base" ? t.accent : t.frame }}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0A0E1A] font-display text-[22px] font-black text-[var(--tc-accent)]">{p.jersey ?? "–"}</span>
          <div className="min-w-0">
            <p className="truncate font-display text-[24px] font-black uppercase leading-none">{p.name ?? "Player"}</p>
            <p className="truncate font-mono-gd text-[10px] font-bold uppercase tracking-wider">{positionsOf(p)} · {teamName ?? "GAMEDAY"}</p>
          </div>
        </div>
        {t.label && (
          <p data-testid="trading-card-edition-reason" className="border-b border-[var(--tc-line)] bg-[var(--tc-soft)] px-3 py-1 font-mono-gd text-[11px] font-bold uppercase tracking-wider text-[var(--tc-accent)]">{t.label}: {ed.reason}</p>
        )}
        <div className="grid grid-cols-4 border-b border-[#1E293B] px-3 py-2 font-mono-gd text-[12px] text-white">
          {(publicView
            ? [["Class", p.grad_year ?? "—"], ["Pos", positionsOf(p)], ["Level", p.level ? String(p.level).toUpperCase() : "—"], ["GP", season?.gp ?? 0]]
            : [["HT", height(p.height_in)], ["WT", p.weight_lb ? `${Math.round(p.weight_lb)}` : "—"], ["GPA", num(p.gpa)], ["Class", p.grad_year ?? "—"]]).map(([l, v]) => (
            <div key={l}><p className="text-[9px] uppercase tracking-wider text-slate-400">{l}</p><p className="font-bold">{v}</p></div>
          ))}
        </div>
        <div className="space-y-2 px-3 pt-2.5">
          <p className="font-mono-gd text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--tc-accent)]">{seasonLabel()} season averages</p>
          <div data-testid="trading-card-averages" className="grid grid-cols-5 gap-1">
            {[["PPG", "ppg", true], ["RPG", "rpg"], ["APG", "apg"], ["SPG", "spg"], ["BPG", "bpg"]].map(([l, k, a]) => <Box key={k} label={l} value={num(s[k])} accent={a} />)}
          </div>
          <div data-testid="trading-card-shooting" className="grid grid-cols-4 gap-1">
            {[["FG%", "fg_pct"], ["3P%", "fg3_pct"], ["FT%", "ft_pct"], ["TS%", "ts_pct"]].map(([l, k]) => <Box key={k} label={l} value={pctOf(s[k])} />)}
          </div>
          <p className="pt-1 font-mono-gd text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--tc-accent)]">Game by game</p>
        </div>
        <div className="mt-1 flex-1 overflow-hidden px-2"><GameLog games={games} s={s} max={maxRows} /></div>
        <div className="flex items-center justify-between border-t border-[#1E293B] px-3 py-1.5 font-mono-gd text-[9px] uppercase tracking-wider text-slate-400">
          <span>{games.length > maxRows ? `Last ${maxRows} of ${games.length} games` : "GAMEDAY Courtside"}</span>
          <span className="font-display text-[13px] font-black text-white">GAME<span className="text-[#FF3B30]">DAY</span></span>
        </div>
      </div>
      <Foil live={live} />
    </div>
  );
});
CardBack.displayName = "CardBack";

/** Pointer (and phone tilt, where the browser allows it) moves the foil and tilts the card. */
const useHolo = () => {
  const ref = useRef(null);
  const set = (px, py, on) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
    el.style.setProperty("--rx", `${(0.5 - py) * 18}deg`);
    el.style.setProperty("--ry", `${(px - 0.5) * 22}deg`);
    el.style.setProperty("--holo", on ? "0.6" : "0.3");
    el.classList.toggle("tc-idle", !on);
  };
  const onPointerMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    set(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)), true);
  };
  const onPointerLeave = () => set(0.5, 0.5, false);
  return { ref, onPointerMove, onPointerLeave };
};

const slug = (n) => (n ?? "player").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const CardDialog = ({ open, onClose, p, teamName, photo, season, perGame, loading, publicView }) => {
  const edition = editionOf(season);
  const glow = THEMES[edition.tier].glow;
  const [flipped, setFlipped] = useState(false);
  const [busy, setBusy] = useState("");
  const holo = useHolo();
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [vw, setVw] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const scale = Math.min(1, (vw - 64) / W);
  const download = async (side) => {
    setBusy(side);
    try {
      await document.fonts.ready;
      const url = await toPng(side === "front" ? frontRef.current : backRef.current, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug(p.name)}-trading-card-${side}.png`;
      a.click();
    } catch {
      toast.error("Couldn't build the image. Try again.");
    } finally {
      setBusy("");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setFlipped(false); } }}>
      <DialogContent data-testid="trading-card-dialog" className="max-w-[420px] border-[#1E293B] bg-[#0A0E1A]/95 p-5 text-white backdrop-blur-xl sm:p-6">
        <DialogTitle className="font-display text-2xl uppercase">Trading card</DialogTitle>
        <DialogDescription className="-mt-2 text-sm text-slate-400">Tap the card to flip it. Move your mouse or finger across it to see the foil.</DialogDescription>
        <div className="tc-stage mx-auto" style={{ width: W * scale, height: H * scale }}>
          <div ref={holo.ref} className="tc-tilt tc-idle touch-none origin-top-left" style={{ width: W, height: H, scale: String(scale) }} onPointerMove={holo.onPointerMove} onPointerLeave={holo.onPointerLeave} onPointerUp={holo.onPointerLeave}>
            <button type="button" data-testid="trading-card-flip-area" onClick={() => setFlipped((f) => !f)} aria-label="Flip card"
              className={`tc-flip block cursor-pointer ${flipped ? "is-flipped" : ""}`} style={{ width: W, height: H }}>
              <div className="tc-face rounded-[18px]" style={{ boxShadow: `0 30px 80px ${glow}` }}><CardFront p={p} teamName={teamName} photo={photo} edition={edition} live /></div>
              <div className="tc-face tc-back rounded-[18px]">
                {loading ? <div className="flex h-full items-center justify-center rounded-[18px] bg-[#0F172A]"><Loader2 className="h-6 w-6 animate-spin text-[#39FF14]" /></div>
                  : <CardBack p={p} teamName={teamName} season={season} perGame={perGame} edition={edition} publicView={publicView} live />}
              </div>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="trading-card-flip-button" onClick={() => setFlipped((f) => !f)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#39FF14]/50 px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-[#39FF14] transition-colors hover:bg-[#39FF14]/10">
            <RotateCcw className="h-4 w-4" />Flip
          </button>
          {[["front", "Front"], ["back", "Back"]].map(([side, label]) => (
            <button key={side} data-testid={`trading-card-download-${side}`} disabled={!!busy || loading} onClick={() => download(side)}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#39FF14] px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0A0E1A] transition-shadow hover:shadow-[0_0_24px_rgba(57,255,20,0.4)] disabled:opacity-50">
              {busy === side ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{label}
            </button>
          ))}
        </div>
        <div aria-hidden className="pointer-events-none fixed left-[-20000px] top-0">
          <CardFront ref={frontRef} p={p} teamName={teamName} photo={photo} edition={edition} />
          <CardBack ref={backRef} p={p} teamName={teamName} season={season} perGame={perGame} edition={edition} publicView={publicView} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const THUMB = 0.36;

/** Small card under the player's profile; opens the big flippable card. Pass season/perGame, or statsPath to load them. */
export const TradingCard = ({ p, teamName, photo, season, perGame, statsPath, autoOpen = false, onClose, publicView = false }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const stats = useApi(statsPath || null);
  const s = statsPath ? stats.data?.season ?? stats.data : season;
  const games = statsPath ? stats.data?.per_game ?? s?.per_game : perGame;
  const edition = editionOf(s);
  return (
    <>
      <button type="button" data-testid="trading-card-thumb" onClick={() => setOpen(true)} aria-label={`Open ${p.name ?? "player"}'s trading card`}
        className="group relative shrink-0 overflow-hidden rounded-[8px] transition-transform duration-300 hover:-translate-y-1 hover:rotate-[-2deg]" style={{ width: W * THUMB, height: H * THUMB }}>
        <div className="tc-idle origin-top-left" style={{ transform: `scale(${THUMB})`, width: W, height: H }}><CardFront p={p} teamName={teamName} photo={photo} edition={edition} live /></div>
        <span className="absolute inset-x-0 bottom-0 bg-[#0A0E1A]/85 py-1 text-center font-mono-gd text-[10px] font-bold uppercase tracking-wider opacity-90 group-hover:opacity-100" style={{ color: THEMES[edition.tier].accent }}>{THEMES[edition.tier].label ?? "Tap to open"}</span>
      </button>
      <CardDialog open={open} onClose={() => { setOpen(false); onClose?.(); }} p={p} teamName={teamName} photo={photo} season={s} perGame={games} loading={!!statsPath && stats.loading && !stats.data} publicView={publicView} />
    </>
  );
};
